import styles from './ServiceTypeTag.module.css'

const PALETTE = [styles.typeTagInfo, styles.typeTagSuccess, styles.typeTagWarning]

/**
 * The colour is a hash of the type's NAME, not its position in any list.
 *
 * The field exposure screen knows the warehouse's service-type vocabulary,
 * but the transactions screen must not — that list comes from an
 * internal-only endpoint and biller users see this tag too. Hashing the
 * string gives both screens the same colour for the same type with no shared
 * data dependency. Collisions are inevitable with three colours and are
 * cosmetic; the multiplier below was picked so the live Core vocabulary
 * spreads well (CASHIN ≠ CASHOUT, and REMITTANCE — the PII-heavy type —
 * gets a colour of its own).
 */
const colourOf = (type: string): string => {
  let h = 0
  for (const c of type) h = (h * 61 + c.charCodeAt(0)) >>> 0
  return PALETTE[h % PALETTE.length] ?? ''
}

export interface ServiceTypeTagProps {
  readonly type: string
  /** Shown in the reserved neutral tag when `type` is empty — the caller's
   *  word for "no restriction" ("Any"). Empty type with no label renders an
   *  em dash, unstyled. */
  readonly anyLabel?: string
}

/** A service type as a coloured tag — the same colour for the same type on
 *  every screen that shows one. */
export function ServiceTypeTag({ type, anyLabel }: ServiceTypeTagProps) {
  if (!type) {
    return anyLabel ? <span className={`${styles.typeTag} ${styles.typeTagAny}`}>{anyLabel}</span> : <span>—</span>
  }
  return <span className={`${styles.typeTag} ${colourOf(type)}`}>{type}</span>
}
